import { useState } from "react"
import { createPortal } from "react-dom"
import { useAuth, useFirestore, useFirestoreCollectionData } from "reactfire"
import { signOut } from "firebase/auth"
import { terminate, collection } from "firebase/firestore"
import { useNavigate } from "react-router-dom"
import { useRol } from "../hooks/useRol"
import { LISTA_UNIDADES, UNIDADES } from "../config/unidades"
import ApoderadosList     from "../components/ApoderadosList"
import ApoderadoModal     from "../components/ApoderadoModal"
import CuotasGlobalesList from "../components/CuotasGlobalesList"
import CuotaGlobalModal   from "../components/CuotaGlobalModal"
import ResumenFinanciero  from "../pages/ResumenFinanciero"
import GestionUsuarios    from "../components/GestionUsuarios"
import Inventario          from "../components/Inventario"
import InventarioModal     from "../components/InventarioModal"
import RegistroActividad   from "../components/RegistroActividad"
import Calendario          from "../components/Calendario"
import CalendarioModal     from "../components/CalendarioModal"
import "../styles/Dashboard.css"

export default function Dashboard() {
  const auth      = useAuth()
  const firestore = useFirestore()
  const { data: todosUsuarios } = useFirestoreCollectionData(
    collection(firestore, "users"), { idField: "id" }
  )
  const cantPendientes = (todosUsuarios || []).filter(u => u.rol === "viewer" || (!u.rol && u.email)).length

  const navigate  = useNavigate()
  const { rol, unidad: unidadPropia, cargando } = useRol()

  const [vista,              setVista]             = useState("apoderados")
  const [sidebarAbierto,     setSidebarAbierto]    = useState(true)
  const [modalApoderado,     setModalApoderado]    = useState(false)
  const [modalCuota,         setModalCuota]        = useState(false)
  const [modalInventario,    setModalInventario]   = useState(false)
  const [modalCalendario,    setModalCalendario]    = useState(false)
  const [apoderadoEditando,  setApoderadoEditando] = useState(null)
  const [unidadSeleccionada, setUnidadSeleccionada] = useState(null)

  // Nunca es null — siempre tiene un valor válido desde el primer render
  const unidadActiva = unidadSeleccionada
    ?? (rol === "admin" ? "bandada" : (unidadPropia ?? "bandada"))

  const cambiarUnidad = (id) => {
    setUnidadSeleccionada(id)
    setVista("apoderados")
    // En móvil cerrar sidebar al seleccionar unidad
    if (window.innerWidth <= 768) setSidebarAbierto(false)
  }

  const logout = async () => {
    try {
      // Terminar Firestore limpia las conexiones y el cache antes de cerrar sesión
      await terminate(firestore)
    } catch (_) {}
    await signOut(auth)
    // Recargar la página para limpiar todo el estado de React y Firestore
    window.location.href = "/login"
  }

  // Admin edita todo. Usuario de unidad solo edita la suya.
  const puedeEditar = rol === "admin" || (rol === "unidad" && unidadActiva === unidadPropia)

  // ── Guards ──────────────────────────────────────────────
  if (cargando) {
    return (
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        height: "100vh", color: "var(--gris)", gap: 12
      }}>
        <div style={{ fontSize: 40 }}>⚜️</div>
        <div>Cargando...</div>
      </div>
    )
  }

  if (!rol || rol === "viewer") {
    return (
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        height: "100vh", color: "var(--gris)",
        gap: 16, textAlign: "center", padding: 32
      }}>
        <div style={{ fontSize: 48 }}>⚜️</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", color: "var(--verde)", fontSize: 22 }}>
          Cuenta pendiente de activación
        </h2>
        <p style={{ maxWidth: 360, lineHeight: 1.6 }}>
          {rol === "viewer"
            ? <>Tu cuenta fue creada correctamente ✓<br />Estás esperando que un administrador te asigne tu unidad. Una vez activada, podrás acceder al sistema.</>
            : <>Tu cuenta fue creada, pero aún no tiene un rol asignado.<br />Contáctate con el administrador para que te asigne tu unidad.</>
          }
        </p>
        <button onClick={logout} style={{
          marginTop: 8, padding: "10px 24px",
          background: "var(--verde)", color: "#fff",
          border: "none", borderRadius: 10, cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif", fontSize: 14
        }}>
          Cerrar sesión
        </button>
      </div>
    )
  }
  // ────────────────────────────────────────────────────────

  const cfgActiva = UNIDADES[unidadActiva] ?? UNIDADES["bandada"]

  return (
    <>
    <div className="dashboard-root">

      {/* Overlay para móvil — cierra el sidebar al tocar fuera */}
      {sidebarAbierto && window.innerWidth <= 768 && (
        <div className="sidebar-overlay" onClick={() => setSidebarAbierto(false)} />
      )}

      {/* ── SIDEBAR ── */}
      <aside className={`sidebar ${sidebarAbierto ? "" : "colapsado"}`}>
        <div className="sidebar-brand">
          <span className="brand-icon">⚜️</span>
          <span className="brand-text">Scout<br />Finanzas</span>
          {/* Botón toggle DENTRO del sidebar, junto al título */}
          <button
            className="sidebar-toggle-inner"
            onClick={() => setSidebarAbierto(p => !p)}
            title={sidebarAbierto ? "Ocultar menú" : "Mostrar menú"}
          >
            ◀
          </button>
        </div>

        <div className="sidebar-section-label">Unidades</div>
        <nav className="sidebar-nav">
          {LISTA_UNIDADES.map(u => (
            <button
              key={u.id}
              className={`nav-item nav-unidad ${unidadActiva === u.id ? "active" : ""}`}
              style={unidadActiva === u.id
                ? { background: u.bg, color: u.texto, borderLeft: `3px solid ${u.color}` }
                : {}
              }
              onClick={() => cambiarUnidad(u.id)}
            >
              <span className="nav-icon">{u.icono}</span>
              <span style={{ flex: 1, textAlign: "left" }}>{u.nombre}</span>
              {rol === "unidad" && u.id === unidadPropia && (
                <span className="nav-badge-propia">●</span>
              )}
            </button>
          ))}
        </nav>

        <div className="sidebar-section-label">Sección</div>
        <nav className="sidebar-nav">
          {[
            { id: "apoderados", icono: "👥", label: unidadActiva === "consejo" ? "Guiadoras y Dirigentes" : "Apoderados" },
            { id: "cuotas",     icono: "💰", label: "Cuotas"     },
            { id: "resumen",    icono: "📊", label: "Resumen"    },
            { id: "inventario", icono: "📦", label: "Inventario" },
            { id: "calendario",  icono: "📅", label: "Calendario"  },
          ].map(v => (
            <button
              key={v.id}
              className={`nav-item ${vista === v.id ? "active" : ""}`}
              onClick={() => setVista(v.id)}
            >
              <span className="nav-icon">{v.icono}</span>
              {v.label}
            </button>
          ))}

          {rol === "admin" && (
            <button
              className={`nav-item ${vista === "actividad" ? "active" : ""}`}
              onClick={() => setVista("actividad")}
            >
              <span className="nav-icon">🕐</span>
              Actividad
            </button>
          )}
          {rol === "admin" && (
            <button
              className={`nav-item ${vista === "usuarios" ? "active" : ""}`}
              onClick={() => setVista("usuarios")}
            >
              <span className="nav-icon">🔑</span>
              <span style={{ flex: 1 }}>Usuarios</span>
              {cantPendientes > 0 && (
                <span style={{
                  background: "#f59e0b", color: "#fff",
                  borderRadius: "999px", fontSize: 11, fontWeight: 700,
                  padding: "1px 7px", minWidth: 20, textAlign: "center",
                }}>
                  {cantPendientes}
                </span>
              )}
            </button>
          )}
        </nav>

        <div className="sidebar-footer">
          <span className="user-email">{auth.currentUser?.email}</span>
          <button className="logout-btn" onClick={logout}>Salir</button>
        </div>
      </aside>

      {/* Botón para reabrir cuando está colapsado */}
      {!sidebarAbierto && (
        <button
          className="sidebar-reabrir"
          onClick={() => setSidebarAbierto(true)}
          title="Mostrar menú"
        >
          ▶
        </button>
      )}

      {/* ── CONTENIDO PRINCIPAL ── */}
      <main className="main-content">

        {/* Header */}
        <header className="main-header">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              className="unidad-pill"
              style={{
                background:  cfgActiva.bg,
                color:       cfgActiva.texto,
                borderColor: cfgActiva.color + "55",
              }}
            >
              <span style={{ fontSize: 18 }}>{cfgActiva.icono}</span>
              <span style={{ fontWeight: 700 }}>{cfgActiva.nombre}</span>
              {!puedeEditar && (
                <span style={{
                  fontSize: 10, fontWeight: 600, textTransform: "uppercase",
                  letterSpacing: "0.06em", opacity: 0.6, marginLeft: 4,
                }}>
                  Solo lectura
                </span>
              )}
            </div>
            <div>
              <h1 className="page-title">
                {vista === "apoderados" && (unidadActiva === "consejo" ? "Guiadoras y Dirigentes" : "Apoderados")}
                {vista === "cuotas"     && "Cuotas"}
                {vista === "resumen"    && "Resumen Financiero"}
                {vista === "inventario" && "Inventario"}
                {vista === "calendario"  && "Calendario de Actividades"}
                {vista === "actividad"  && "Registro de Actividad"}
                {vista === "usuarios"   && "Gestión de Usuarios"}
              </h1>
              <p className="page-subtitle">
                {vista === "apoderados" && (unidadActiva === "consejo" ? "Miembros del Consejo de Grupo" : "Gestiona los apoderados y sus scouts")}
                {vista === "cuotas"     && "Mensualidades y eventos de la unidad"}
                {vista === "resumen"    && "Estado financiero de la unidad"}
                {vista === "inventario" && "Materiales y equipamiento de la unidad"}
                {vista === "calendario"  && (unidadActiva === "consejo" ? "Todos los eventos de todas las unidades" : "Eventos y actividades de la unidad")}
                {vista === "actividad"  && "Últimos 7 días de movimientos del grupo"}
                {vista === "usuarios"   && "Accesos por unidad"}
              </p>
            </div>
          </div>

          {/* Botones de acción — solo si puede editar */}
          {puedeEditar && vista !== "usuarios" && (
            <div style={{ display: "flex", gap: 10 }}>
              {vista === "apoderados" && (
                <button
                  className="btn-primary"
                  onClick={() => {
                    setApoderadoEditando(null)
                    setModalApoderado(true)
                  }}
                >
                  {unidadActiva === "consejo" ? "+ Nuevo miembro" : "+ Nuevo Apoderado"}
                </button>
              )}
              {vista === "cuotas" && (
                <button
                  className="btn-primary"
                  onClick={() => setModalCuota(true)}
                >
                  + Nueva Cuota
                </button>
              )}
              {vista === "inventario" && (
                <button
                  className="btn-primary"
                  onClick={() => setModalInventario(true)}
                >
                  + Nuevo ítem
                </button>
              )}
              {vista === "calendario" && puedeEditar && unidadActiva !== "consejo" && (  /* botón por unidad — consejo tiene su propio botón interno */
                <button
                  className="btn-primary"
                  onClick={() => setModalCalendario(true)}
                >
                  + Nuevo evento
                </button>
              )}
            </div>
          )}
        </header>

        {/* Vistas */}
        {vista === "apoderados" && (
          <ApoderadosList
            unidadId={unidadActiva}
            puedeEditar={puedeEditar}
            onEditar={(ap) => {
              setApoderadoEditando(ap)
              setModalApoderado(true)
            }}
          />
        )}
        {vista === "cuotas" && (
          <CuotasGlobalesList
            unidadId={unidadActiva}
            puedeEditar={puedeEditar}
          />
        )}
        {vista === "resumen" && (
          <ResumenFinanciero unidadId={unidadActiva} />
        )}
        {vista === "inventario" && (
          <Inventario
            unidadId={unidadActiva}
            puedeEditar={puedeEditar}
            esAdmin={rol === "admin"}
            onNuevoItem={() => setModalInventario(true)}
          />
        )}
        {vista === "calendario" && (
          <Calendario
            unidadId={unidadActiva}
            puedeEditar={puedeEditar}
          />
        )}
        {vista === "actividad" && rol === "admin" && (
          <RegistroActividad />
        )}
        {vista === "usuarios" && rol === "admin" && (
          <GestionUsuarios />
        )}

      </main>

    </div>

    {/* ── MODALES: montados en body via portal, nunca cortados por overflow ── */}
    {modalApoderado && createPortal(
      <ApoderadoModal
        unidadId={unidadActiva}
        apoderado={apoderadoEditando}
        onCerrar={() => {
          setModalApoderado(false)
          setApoderadoEditando(null)
        }}
      />,
      document.body
    )}

    {modalCalendario && createPortal(
      <CalendarioModal
        unidadId={unidadActiva}
        fecha={new Date()}
        evento={null}
        onCerrar={() => setModalCalendario(false)}
      />,
      document.body
    )}

    {modalInventario && createPortal(
      <InventarioModal
        unidadId={unidadActiva}
        item={null}
        onCerrar={() => setModalInventario(false)}
      />,
      document.body
    )}

    {modalCuota && createPortal(
      <CuotaGlobalModal
        unidadId={unidadActiva}
        onCerrar={() => setModalCuota(false)}
      />,
      document.body
    )}
  </>
  )
}
